/** @jsx jsx */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cssMap, css, jsx } from '@compiled/react';
import { Field } from '@atlaskit/form';
import { Text, Box } from '@atlaskit/primitives/compiled';
import { Grid } from '@atlaskit/primitives';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import LockUnlockedIcon from '@atlaskit/icon/core/lock-unlocked';
import { token } from '@atlaskit/tokens';
import Select, {
	components,
	type CSSObjectWithLabel,
	type ControlProps,
	type OptionProps,
	type ValueContainerProps,
} from '@atlaskit/select';
import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import type {
	GetProjectAccessResponse,
	ProjectAccessLevel,
} from '../../services/use-project-access-level/types.tsx';

const FIELD_NAME = 'projectAccessLevel';

const styles = cssMap({
	iconContainer: {
		gridArea: 'icon',
		paddingInline: token('space.100'),
		alignSelf: 'center',
	},
	dropdownContainer: {
		gridArea: 'dropdown',
	},
	descriptionContainer: {
		gridArea: 'description',
		width: 'fit-content',
	},
	field: {
		marginBottom: token('space.150'),
	},
	skeleton: {
		marginBottom: token('space.150'),
	},
});
// `css` is used to customise the styles of the Select components, `cssMap` / `xcss` don't work here
const controlStyles = css({
	width: 'fit-content',
});
const valueContainerStyles = css({
	font: token('font.body.large'),
	fontWeight: token('font.weight.bold'),
});

const messages = defineMessages({
	projectAccessLevelLabel: {
		id: 'share-dialog.fields.ProjectAccessLevelField.project-access-level-label.non-final',
		defaultMessage: 'Project access',
		description: 'Label for the project access level field',
	},
});

type ProjectAccessLevelSelectOption = {
	label: ProjectAccessLevel['displayName'];
	value: ProjectAccessLevel['value'];
	iconName: ProjectAccessLevel['iconName'];
	description: ProjectAccessLevel['description'];
};

export type ProjectAccessLevelFieldValues = {
	[FIELD_NAME]: ProjectAccessLevel;
};

const SelectControl = (props: ControlProps<ProjectAccessLevelSelectOption>) => {
	return <components.Control {...props} appearance="subtle" isCompact css={controlStyles} />;
};

const SelectValueContainer = (props: ValueContainerProps<ProjectAccessLevelSelectOption>) => {
	return <components.ValueContainer {...props} css={valueContainerStyles} />;
};

const SelectOption = ({ children, ...props }: OptionProps<ProjectAccessLevelSelectOption>) => {
	const { data } = props;

	if (!data) {
		return null;
	}

	return (
		<components.Option {...props}>
			<Grid templateColumns="auto 1fr" templateAreas={['icon dropdown', 'blank description']}>
				<Box xcss={styles.iconContainer}>{getIcon(data.iconName)}</Box>
				<Box xcss={styles.dropdownContainer}>
					<Text size="large" weight="bold">
						{data.label}
					</Text>
				</Box>
				<Box xcss={styles.descriptionContainer}>
					<Text color="color.text.subtle">{data.description}</Text>
				</Box>
			</Grid>
		</components.Option>
	);
};

export const getIcon = (icon?: string) => {
	const localProps = {
		label: '',
		color: token('color.icon.accent.gray'),
		LEGACY_size: 'medium' as const,
		spacing: 'spacious' as const,
	};
	switch (icon) {
		case 'open':
			return <LockUnlockedIcon {...localProps} />;
		case 'private':
			return <LockLockedIcon {...localProps} />;
		default:
			return (
				<PeopleGroupIcon
					label={localProps.label}
					color={localProps.color}
					size={localProps.LEGACY_size}
				/>
			);
	}
};

const transformToSelectOption = (level: ProjectAccessLevel): ProjectAccessLevelSelectOption => ({
	label: level.displayName,
	value: level.value,
	iconName: level.iconName,
	description: level.description,
});

type Props = {
	currentAccessLevel?: ProjectAccessLevel;
	accessLevels?: ProjectAccessLevel[];
	getProjectAccessLevels: () => Promise<GetProjectAccessResponse | undefined>;
	isLoading?: boolean;
};

export const ProjectAccessLevelSelect = ({
	currentAccessLevel,
	accessLevels,
	getProjectAccessLevels,
	isLoading,
}: Props) => {
	const { formatMessage } = useIntl();
	const selectContainerRef = useRef<HTMLDivElement | null>(null);

	const [currentValue, setCurrentValue] = useState<ProjectAccessLevelSelectOption | undefined>(
		currentAccessLevel && transformToSelectOption(currentAccessLevel),
	);

	const selectOptions = useMemo(
		() => accessLevels?.map((level) => transformToSelectOption(level)),
		[accessLevels],
	);

	useEffect(() => {
		!currentValue &&
			!isLoading &&
			currentAccessLevel &&
			setCurrentValue(transformToSelectOption(currentAccessLevel));
	}, [currentAccessLevel, currentValue, isLoading]);

	useEffect(() => {
		!accessLevels && !isLoading && getProjectAccessLevels();
	}, [accessLevels, getProjectAccessLevels, isLoading]);

	if (!accessLevels && isLoading) {
		return (
			<Box xcss={styles.skeleton}>
				<Skeleton height="20px" />
			</Box>
		);
	}

	return (
		<Box xcss={styles.field}>
			<Field<ProjectAccessLevelSelectOption>
				name={FIELD_NAME}
				label={formatMessage(messages.projectAccessLevelLabel)}
				defaultValue={currentValue}
			>
				{({ fieldProps: { onChange, ...fieldProps } }) => (
					<Grid templateColumns="auto 1fr" templateAreas={['icon dropdown', 'blank description']}>
						<Box xcss={styles.iconContainer} testId="share-button.ui.project-access-level.box">
							{getIcon(currentValue?.iconName)}
						</Box>
						<Box xcss={styles.dropdownContainer} ref={selectContainerRef}>
							<Select<ProjectAccessLevelSelectOption>
								{...fieldProps}
								options={selectOptions}
								onChange={(value: ProjectAccessLevelSelectOption | null) => {
									if (!value) {
										return;
									}

									setCurrentValue(value);
									onChange(value);
								}}
								isSearchable={false}
								menuPortalTarget={globalThis.document.body}
								components={{
									Control: SelectControl,
									ValueContainer: SelectValueContainer,
									Option: SelectOption,
								}}
								// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
								styles={{
									menuPortal: (base: CSSObjectWithLabel) => ({
										...base,
										zIndex: 9999,
										width: selectContainerRef.current?.offsetWidth,
									}),
								}}
								menuPosition="fixed"
							/>
						</Box>
						<Box xcss={styles.descriptionContainer}>
							<Text color="color.text.subtle">{currentValue?.description}</Text>
						</Box>
					</Grid>
				)}
			</Field>
		</Box>
	);
};
